import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactGA from 'react-ga4'
import { LiveChatWidget } from '@livechat/widget-react'

import { useAppDispatch, useAppSelector } from './store'
import api from './api'
import { clearLoginData } from './store/authSlice'
import { InitialStateType, setLiveChatVisibility } from './store/liveChatSlice'
import { AppState, setIsProduction } from './store/appSlice'

import ComingSoon from './components/v2/buttons/ComingSoon'
import Loader from './components/Loader'
import AppRoutes from './routes'
import i18n from './i18n'
import { DayjsLocalManager } from './screens/hooks/DayjsLocalManager'

const GOOGLE_TRACKING_ID = 'G-VLBVDQWE11'
const PRODUCTION_DOMAINS = new Set(['frani.com', 'www.frani.com'])
const DEV_DOMAIN = 'dev.frani.com'
const ALLOWED_PIN_CODES = new Set(['10408', null])

ReactGA.initialize(GOOGLE_TRACKING_ID)

export const UserPrivateRoute = ({ children }: { children: ReactNode }): JSX.Element | null => {
  const auth = useAppSelector((data) => data.auth)

  if (auth.userLoading) {
    return <Loader />
  }

  if (auth.token && auth.user?.userType === 'User') {
    return <>{children}</>
  }

  return <Navigate replace to="/login" />
}

const App = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const auth = useAppSelector((data) => data.auth)
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const { licenseKey, visibility, group } = useAppSelector((data: { liveChat: InitialStateType }) => data.liveChat)

  const [pinCode] = useState<string | null>(() => localStorage.getItem('pin'))
  const hostname = window.location.hostname
  const isDevDomain = hostname === DEV_DOMAIN
  const showPinScreen = isDevDomain && ALLOWED_PIN_CODES.has(pinCode)

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = i18n?.language
  }, [])

  // Google Analytics pageview tracking
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname,
      title: window.location.pathname.replace(/\//g, ' ').replace(/-/g, ' '),
    })
  }, [])

  // Load user profile by token
  useEffect(() => {
    if (!auth.token) return

    const loadProfile = async () => {
      const { error: loadProfileError } = await dispatch(api.endpoints.profile.initiate({ token: auth.token }))

      if (loadProfileError) {
        dispatch(clearLoginData())
      }
    }

    loadProfile()
  }, [auth.token, dispatch])

  // Set dayjs locale
  useEffect(() => {
    const dayjsLocale = lang === 'ge' ? 'ka' : lang
    DayjsLocalManager(dayjsLocale)
  }, [lang])

  // Set production environment flag
  useEffect(() => {
    const isProduction = PRODUCTION_DOMAINS.has(hostname)
    dispatch(setIsProduction(isProduction))
  }, [dispatch, hostname])

  const handleLiveChatVisibilityChange = useCallback(
    (event: { visibility: InitialStateType['visibility'] }) => {
      dispatch(setLiveChatVisibility(event.visibility))
    },
    [dispatch],
  )

  if (showPinScreen) {
    return (
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <Routes>
          <Route path="*" element={<ComingSoon title={t('pinTitle')} message={t('pinDescription')} />} />
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <>
      <LiveChatWidget
        license={licenseKey}
        group={group}
        visibility={visibility}
        onVisibilityChanged={handleLiveChatVisibilityChange}
      />
      <AppRoutes />
    </>
  )
}

export default App
